/**
 * AdminProfessionalsConfig - Blueprint CMS Panel
 * ==============================================
 * Manages all text content for the Retail/Fabricator/Advisor
 * section. All values stored in blueprint_professionals_config table.
 * 
 * @version 1.0
 */

import React, { useState, useEffect } from 'react';
import { Save, Loader2, RefreshCw, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import supabase from '../../lib/supabase';

const AdminProfessionalsConfig = () => {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLang, setSelectedLang] = useState('it');
  
  // Load config
  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('blueprint_professionals_config')
        .select('*')
        .eq('lang', selectedLang)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setConfig(data);
      } else {
        // Create default config for this language
        const { data: newConfig } = await supabase
          .from('blueprint_professionals_config')
          .insert({ lang: selectedLang })
          .select()
          .single();
        setConfig(newConfig);
      }
    } catch (err) {
      console.error('Error loading config:', err);
      toast.error('Errore nel caricamento');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadConfig();
  }, [selectedLang]);
  
  // Save config
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('blueprint_professionals_config')
        .update(config)
        .eq('id', config.id);
      
      if (error) throw error;
      toast.success('Configurazione salvata!');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Update field
  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };
  
  // Form field component
  const FormField = ({ label, field, type = 'text', rows = 1 }) => (
    <div className="space-y-1.5">
      <label className="text-sm text-white/60">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={config?.[field] || ''}
          onChange={(e) => updateField(field, e.target.value)}
          rows={rows}
          className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none resize-none text-sm"
        />
      ) : (
        <input
          type="text"
          value={config?.[field] || ''}
          onChange={(e) => updateField(field, e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none text-sm"
        />
      )}
    </div>
  );
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Configurazione Retail & Advisor</h2>
          <p className="text-white/50 text-sm mt-1">
            Gestisci tutti i testi della sezione professionisti
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Language selector */}
          <div className="flex items-center gap-2 bg-[#1a1a1a] rounded-lg p-1">
            <Globe className="w-4 h-4 text-white/40 ml-2" />
            {['it', 'en'].map(lang => (
              <button
                key={lang}
                onClick={() => setSelectedLang(lang)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  selectedLang === lang 
                    ? 'bg-[#B08B5C] text-black' 
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          
          <Button
            variant="outline"
            onClick={loadConfig}
            className="border-[#1a1a1a] text-white/60"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </div>
      </div>
      
      {/* Content sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Page Content */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Pagina principale</h3>
          <div className="space-y-4">
            <FormField label="Titolo pagina" field="page_title" />
            <FormField label="Sottotitolo pagina" field="page_subtitle" type="textarea" rows={2} />
            <FormField label="Placeholder ricerca" field="search_placeholder" />
            <FormField label="Messaggio nessun risultato" field="no_results_message" />
          </div>
        </div>
        
        {/* Filters */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Filtri e tipi</h3>
          <div className="space-y-4">
            <FormField label="Label: Tutti" field="filter_all_label" />
            <FormField label="Label: Rivenditore" field="filter_retailer_label" />
            <FormField label="Label: Fabbricante" field="filter_fabricator_label" />
            <FormField label="Label: Advisor" field="filter_advisor_label" />
            <FormField label="Label: Showroom" field="filter_showroom_label" />
          </div>
        </div>
        
        {/* Card labels */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Card professionista</h3>
          <div className="space-y-4">
            <FormField label="Pulsante: Contatta" field="contact_button" />
            <FormField label="Pulsante: Vedi dettagli" field="details_button" />
            <FormField label="Label: Disponibile" field="available_label" />
            <FormField label="Label: Verificato" field="verified_label" />
            <FormField label="Label: Recensioni" field="reviews_label" />
            <FormField label="Label: km" field="distance_label" />
          </div>
        </div>
        
        {/* Modal labels */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Modal dettaglio</h3>
          <div className="space-y-4">
            <FormField label="Label: Specializzazioni" field="modal_specializations" />
            <FormField label="Label: Servizi" field="modal_services" />
            <FormField label="Label: Disponibilità" field="modal_availability" />
            <FormField label="Label: Orari" field="modal_hours" />
            <FormField label="Label: Indirizzo" field="modal_address" />
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Pulsanti azione</h3>
          <div className="space-y-4">
            <FormField label="Richiedi informazioni" field="request_info_button" />
            <FormField label="Richiedi foto lastra" field="request_photos_button" />
            <FormField label="Chiama" field="call_button" />
            <FormField label="WhatsApp" field="whatsapp_button" />
            <FormField label="Email" field="email_button" />
            <FormField label="Sito web" field="website_button" />
            <FormField label="Vedi lastre" field="view_slabs_button" />
          </div>
        </div>
        
        {/* Stats */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Statistiche</h3>
          <div className="space-y-4">
            <FormField label="Richieste ricevute" field="stats_requests" />
            <FormField label="Tasso risposta" field="stats_response_rate" />
            <FormField label="Tempo medio" field="stats_avg_response" />
            <FormField label="Valutazione media" field="stats_rating" />
          </div>
        </div>
        
        {/* Material section */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">Sezione "Dove trovare" (Scheda materiale)</h3>
          <div className="space-y-4">
            <FormField label="Titolo sezione" field="material_section_title" />
            <FormField label="Sottotitolo sezione" field="material_section_subtitle" type="textarea" rows={2} />
          </div>
        </div>
        
        {/* CTA */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5">
          <h3 className="text-white font-medium mb-4">CTA Professionisti</h3>
          <div className="space-y-4">
            <FormField label="Titolo CTA" field="cta_professional_title" />
            <FormField label="Sottotitolo CTA" field="cta_professional_subtitle" type="textarea" rows={2} />
            <FormField label="Pulsante registrazione" field="cta_register_button" />
          </div>
        </div>
        
        {/* Photo request */}
        <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-5 lg:col-span-2">
          <h3 className="text-white font-medium mb-4">Modal richiesta foto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Titolo modal" field="photo_request_title" />
            <FormField label="Sottotitolo modal" field="photo_request_subtitle" type="textarea" rows={2} />
          </div>
          <div className="mt-4">
            <label className="text-sm text-white/60 block mb-2">
              Opzioni richiesta foto (JSON array)
            </label>
            <textarea
              value={typeof config?.photo_request_items === 'string' 
                ? config.photo_request_items 
                : JSON.stringify(config?.photo_request_items || [], null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  updateField('photo_request_items', parsed);
                } catch {
                  // Keep as string if not valid JSON
                  updateField('photo_request_items', e.target.value);
                }
              }}
              rows={8}
              className="w-full px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#1a1a1a] text-white font-mono text-xs focus:border-[#B08B5C] focus:outline-none resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfessionalsConfig;
